import { fetchEventSource } from '@microsoft/fetch-event-source';
import { CONFIG } from '../config';

export interface SseEvent {
  event: 'meta' | 'delta' | 'done' | 'error';
  data: unknown;
}

export interface AskRequest {
  query: string;
  newConversation?: boolean;
  threadKey?: string;
  verbosity?: string;
}

export interface SseError {
  status?: number;
  retryAfter?: number;
  errorCode?: string;
  message: string;
}

interface StreamOptions {
  token: string;
  body: AskRequest;
  signal: AbortSignal;
  onEvent: (event: SseEvent) => void;
  onError: (error: SseError) => void;
}

export async function streamAsk(options: StreamOptions): Promise<void> {
  const { token, body, signal, onEvent, onError } = options;

  // fetchEventSource calls onerror after the throw from onopen, so without a
  // dedup flag we'd report the error twice — once with the right HTTP status
  // (503/429/...), then again with a generic "Error: /ask 503" message that
  // clobbers the typed notice. Latch on the first onError call.
  let reported = false;
  const report = (err: SseError) => {
    if (reported) return;
    reported = true;
    onError(err);
  };

  await fetchEventSource(`${CONFIG.API_BASE}/ask`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
    },
    credentials: 'include',
    body: JSON.stringify(body),
    signal,
    openWhenHidden: true,
    async onopen(res) {
      if (res.ok) return;
      // Server returns a problem-details-ish body on known failure modes
      // (kairos_paused, content_filter, etc.) — read it so the SPA can show
      // the right notice variant. Fall back to statusText if body is missing
      // or non-JSON.
      const retryAfter = res.headers.get('Retry-After');
      let errorCode: string | undefined;
      let message = res.statusText;
      try {
        const body = await res.json();
        if (body && typeof body === 'object') {
          if (typeof body.error === 'string') errorCode = body.error;
          if (typeof body.message === 'string' && body.message) message = body.message;
        }
      } catch {
        /* non-JSON response — keep statusText */
      }
      report({
        status: res.status,
        retryAfter: retryAfter && /^\d+$/.test(retryAfter) ? parseInt(retryAfter, 10) : undefined,
        errorCode,
        message,
      });
      throw new Error(`/ask ${res.status}`);
    },
    onmessage(msg) {
      onEvent({
        event: (msg.event || 'delta') as SseEvent['event'],
        data: msg.data ? JSON.parse(msg.data) : null,
      });
    },
    onerror(err) {
      // Either we already reported a typed status from onopen (paused / rate
      // limited / etc.) or this is a fresh network/parse error. Either way,
      // stop retrying — fetchEventSource defaults to retry on throw.
      report({ message: err instanceof Error ? err.message : String(err) });
      throw err;
    },
  });
}
